/**
 * The selected patient in the Velora Modern top bar (patient/current.ts),
 * always on the dark chrome: name (masked in privacy mode), chart number,
 * age, alert and premed badges when the selecting screen had them, and
 * Unselect (the button, Escape while focus is on the bar, or Alt+Shift+X
 * anywhere). With nobody selected it reads "No patient selected" and opens
 * the patient search.
 *
 * SelectedPatientSync keeps the store honest: opening a patient's page
 * selects that patient (labels come from the page or this tab's recent
 * list; nothing is fetched here), and switching office drops a patient the
 * new scope does not hold.
 */
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { Badge } from '@velora/ui';
import { Search, UserRound, X } from 'lucide-react';
import { useEffect, useRef, type KeyboardEvent } from 'react';
import { useOfficeScope } from '../office/OfficeScope.js';
import {
  clearCurrentPatient,
  getCurrentPatient,
  inOfficeScope,
  setCurrentPatient,
  useCurrentPatient,
} from '../patient/current.js';
import { patientInUrl } from '../patient/follow.js';
import { usePrivacy } from '../privacy/privacy.js';
import { usePermission } from '../session/session.js';
import { useShortcut } from '../shortcuts/registry.js';

export const UNSELECT_KEYS = 'alt+shift+x';

export interface PatientBarProps {
  /** Opens the patient search (the palette in patient mode). */
  onSearch: () => void;
  /** After Unselect: where to go when the screen was one patient's page. */
  onUnselected?: () => void;
}

export function PatientBar({ onSearch, onUnselected }: PatientBarProps) {
  const patient = useCurrentPatient();
  const privacy = usePrivacy();
  const canView = usePermission('patient.view');
  const unselect = () => {
    if (!getCurrentPatient()) return;
    clearCurrentPatient();
    onUnselected?.();
  };
  useShortcut(UNSELECT_KEYS, 'Unselect patient', unselect, {
    section: 'Global',
    id: 'shell.patient.unselect',
    enabled: patient !== null,
  });

  if (!canView) return null;
  if (!patient) {
    return (
      <div className="shell-patientbar is-empty" role="group" aria-label="Selected patient">
        <button
          type="button"
          className="shell-patientbar__find"
          onClick={onSearch}
          title="Find a patient to select"
        >
          <UserRound size={14} aria-hidden />
          <span className="shell-patientbar__none">No patient selected</span>
          <Search size={13} aria-hidden className="shell-patientbar__findicon" />
        </button>
      </div>
    );
  }

  const name = patient.label ? privacy.mask(patient.label) : 'Selected patient';
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && !e.defaultPrevented) {
      e.preventDefault();
      e.stopPropagation();
      unselect();
    }
  };
  return (
    <div
      className="shell-patientbar"
      role="group"
      aria-label="Selected patient"
      data-patient-id={patient.id}
      onKeyDown={onKeyDown}
    >
      <Link
        to="/patients/$patientId"
        params={{ patientId: patient.id }}
        preload={false}
        className="shell-patientbar__who"
        title="Open the patient's family file"
      >
        <UserRound size={14} aria-hidden className="shell-patientbar__icon" />
        <span className="shell-patientbar__name" data-phi="">
          {name}
        </span>
        {patient.chartNumber ? (
          <span className="shell-patientbar__meta" aria-label={`Chart ${patient.chartNumber}`}>
            #{patient.chartNumber}
          </span>
        ) : null}
        {typeof patient.age === 'number' ? (
          <span className="shell-patientbar__meta" aria-label={`Age ${patient.age}`}>
            {patient.age}y
          </span>
        ) : null}
      </Link>
      {patient.hasAlert ? (
        <Badge tone="danger" className="shell-patientbar__badge">
          Alert
        </Badge>
      ) : null}
      {patient.premed ? (
        <Badge tone="brand" className="shell-patientbar__badge">
          Premed
        </Badge>
      ) : null}
      <button
        type="button"
        className="shell-patientbar__clear"
        onClick={unselect}
        aria-label="Unselect patient"
        aria-keyshortcuts="Alt+Shift+X"
        title="Unselect patient (Esc on the bar, Alt+Shift+X)"
      >
        <X size={14} aria-hidden />
        <span className="shell-patientbar__cleartext">Unselect</span>
      </button>
    </div>
  );
}

/** Selects the patient a URL opens, and drops one the office scope no longer holds. */
export function SelectedPatientSync() {
  const location = useLocation();
  const urlPatient = patientInUrl(location.pathname, location.search as Record<string, unknown>);
  useEffect(() => {
    if (!urlPatient || getCurrentPatient()?.id === urlPatient) return;
    // The page publishes the label once it has loaded the patient.
    setCurrentPatient({ id: urlPatient, label: '', chartNumber: null });
  }, [urlPatient]);

  const office = useOfficeScope();
  const scopeKey = office.isAll ? '*' : office.locationIds.join(',');
  const prevScope = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevScope.current;
    prevScope.current = scopeKey;
    if (prev === null || prev === scopeKey) return;
    const p = getCurrentPatient();
    if (!p || office.isAll) return;
    // Home office unknown but their page is open in the new scope (the page
    // and its audited API decide what this scope may see): keep them.
    if (p.homeLocationId === undefined && p.id === urlPatient) return;
    if (!inOfficeScope(p, office.locationIds)) clearCurrentPatient();
    // `office` changes identity every render; the key captures it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);
  return null;
}

/** Unselect while on one patient's page: go to that module's own list. */
export function useLeavePatientPage(listHref: string | undefined): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  const latest = useRef({ listHref, location });
  latest.current = { listHref, location };
  return () => {
    const { listHref: href, location: loc } = latest.current;
    if (!patientInUrl(loc.pathname, loc.search as Record<string, unknown>)) return;
    void navigate({ to: (href ?? '/') as never });
  };
}
